import { Directive, ElementRef, EventEmitter, OnDestroy, Output } from '@angular/core';
import { fromEvent, merge, of, Subscription, timer } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

@Directive({
  selector: '[mfpLongPress]'
})
export class LongPressDirective implements OnDestroy {
  private eventSubscribe: Subscription;
  threshold = 500;
  timesBeforeSpeedUpX2 = 10;
  timesBeforeSpeedUpX3 = 20;

  @Output()
  mouseLongPress = new EventEmitter<number>();

  constructor(private elementRef: ElementRef) {
    const mousedown = fromEvent<MouseEvent>(elementRef.nativeElement, 'mousedown').pipe(
      filter(event => event.button === 0), // Only allow left button (Primary button)
      map(event => true) // turn on threshold counter
    );
    const touchstart = fromEvent(elementRef.nativeElement, 'touchstart').pipe(map(() => true));
    const touchend = fromEvent(elementRef.nativeElement, 'touchend').pipe(map(() => null));
    const mouseout = fromEvent(elementRef.nativeElement, 'mouseout').pipe(map(() => null));
    const mouseup = fromEvent<MouseEvent>(elementRef.nativeElement, 'mouseup').pipe(
      filter(event => event.button === 0), // Only allow left button (Primary button)
      map(() => null) // reset threshold counter
    );
    this.eventSubscribe = merge(mousedown, mouseup, touchstart, touchend, mouseout)
      .pipe(
        switchMap(state => (state ? timer(this.threshold, 100) : of(state))),
        switchMap(value => {
          let newObservable$ = of(value);
          if (value) {
            if (value > this.timesBeforeSpeedUpX3) {
              newObservable$ = timer(0, 25);
            } else if (value > this.timesBeforeSpeedUpX2) {
              newObservable$ = timer(0, 50);
            }
          }
          return newObservable$.pipe(
            // Just discard the new generated values, we only care about the first one
            map(v => value)
          );
        })
      )
      .subscribe(value => {
        if (value !== null) {
          this.mouseLongPress.emit(value || 0);
        }
      });
  }

  ngOnDestroy(): void {
    if (this.eventSubscribe) {
      this.eventSubscribe.unsubscribe();
    }
  }
}
